/**
 * GalleryImageCard
 *
 * Einzelne Bildkarte für Grid-Ansicht.
 * Vereinfachte Version mit Hover-Aktionen.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthImageUrl } from '@/hooks/useAuthImageUrl';
import {
  Heart,
  Search,
  Layers,
  Download,
  Globe,
  Trash2,
  Check,
  AlertTriangle,
  Tag,
  Folder,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useImageActions } from '@/hooks/useImageActions';
import { useApprovalActions } from '@/hooks/useApproval';
import { useImageRating } from '@/hooks/useImageRating';
import { ImageRatingInput } from '../rating/ImageRatingInput';
import { cn } from '@/lib/utils';
import type { GalleryImage } from '@/lib/api/gallery-v2';

interface GalleryImageCardProps {
  image: GalleryImage;
  isSelected: boolean;
  onToggleSelect: () => void;
  onToggleFavorite: () => void;
  onClick?: () => void;
  variant?: 'grid' | 'bento';
  showApprovalActions?: boolean;
}

export function GalleryImageCard({
  image,
  isSelected,
  onToggleSelect,
  onToggleFavorite,
  onClick,
  variant = 'grid',
  showApprovalActions = false,
}: GalleryImageCardProps) {
  const { t } = useTranslation();
  const { authUrl } = useAuthImageUrl();
  const [showActions, setShowActions] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {
    deleteImage,
    publishImage,
    downloadImage,
    generateVariation,
    isDeleting,
    isPublishing,
    isDownloading,
    isGeneratingVariation,
  } = useImageActions();

  const { updateSingle, isUpdatingSingle } = useApprovalActions();

  const { mutate: rateImage, isPending: isRatingPending } = useImageRating(image.id);

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateSingle({ imageId: image.id, action: 'approve' });
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateSingle({ imageId: image.id, action: 'reject' });
  };

  const handleRatingChange = (rating: number) => {
    rateImage({ rating });
  };

  const displayName =
    image.display_name || image.prompt?.slice(0, 30) || 'Unbenannt';

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/goki-image-id', image.id);
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDelete = () => {
    deleteImage(image.id);
    setShowConfirmDelete(false);
  };

  const handlePublish = () => {
    publishImage(image.id);
    setShowConfirmPublish(false);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(
        'group relative rounded-xl overflow-hidden flex flex-col',
        'bg-neutral-100 dark:bg-neutral-800',
        'transition-all duration-200',
        'hover:shadow-lg',
        variant === 'bento' && 'h-full',
        isSelected && 'ring-4 ring-orange-500',
        isDragging && 'opacity-50'
      )}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        setShowActions(false);
        setShowConfirmDelete(false);
        setShowConfirmPublish(false);
      }}
    >
      {/* Bild-Container – behält aspect-ratio */}
      <div className={cn(
        'relative overflow-hidden',
        variant === 'grid' ? 'aspect-square' : 'flex-1'
      )}>
        {/* Bild - Thumbnail für schnelles Laden */}
        <img
          src={authUrl(`/thumb/${image.id}`)}
          alt={image.alt_title || displayName}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Checkbox oben links - immer sichtbar */}
        <button
          role="checkbox"
          aria-checked={isSelected}
          aria-label={t('gallery.selectImage', { name: displayName })}
          className={cn(
            'absolute top-2 left-2 z-20',
            'w-6 h-6 rounded',
            'flex items-center justify-center',
            'border-2 transition-all',
            isSelected
              ? 'bg-orange-500 border-orange-500 text-white'
              : 'bg-white/90 border-neutral-300 hover:border-orange-400',
            'shadow-sm'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect();
          }}
        >
          {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
        </button>

        {/* Favorit oben rechts - immer sichtbar */}
        <button
          className={cn(
            'absolute top-2 right-2 z-20',
            'p-1.5 rounded-full',
            'transition-all duration-200 ease-out',
            'hover:scale-110 active:scale-95',
            'shadow-md',
            image.is_favorite
              ? 'bg-gradient-to-br from-rose-400 to-red-500 text-white shadow-red-500/30'
              : 'bg-white/95 text-neutral-400 hover:text-rose-500 hover:shadow-lg'
          )}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
        >
          <Heart
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              image.is_favorite && 'fill-current scale-110'
            )}
          />
        </button>

        {/* Approval Status Badge - unten links */}
        {showApprovalActions && image.approval_status && (
          <div
            className={cn(
              'absolute bottom-2 left-2 z-20',
              'px-2 py-1 rounded-full text-xs font-medium',
              'shadow-md',
              image.approval_status === 'approved' && 'bg-green-500 text-white',
              image.approval_status === 'rejected' && 'bg-red-500 text-white',
              image.approval_status === 'pending' && 'bg-orange-500 text-white'
            )}
          >
            {image.approval_status === 'approved' && t('common.approve')}
            {image.approval_status === 'rejected' && t('common.reject')}
            {image.approval_status === 'pending' && t('common.pending')}
          </div>
        )}

        {/* Hover Overlay */}
        {showActions && !showConfirmDelete && !showConfirmPublish && (
          <div className="absolute inset-0 z-10 bg-black/50 flex items-center justify-center">
            {/* Oben Mitte: Approval Actions */}
            {showApprovalActions && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-2">
                <button
                  className="px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                  onClick={handleReject}
                  disabled={isUpdatingSingle}
                  title={t('common.reject')}
                >
                  <XCircle className="h-4 w-4" />
                  {t('common.reject')}
                </button>
                <button
                  className="px-3 py-1.5 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                  onClick={handleApprove}
                  disabled={isUpdatingSingle}
                  title={t('common.approve')}
                >
                  <CheckCircle className="h-4 w-4" />
                  {t('common.approve')}
                </button>
              </div>
            )}

            {/* Oben rechts: Variation + Download */}
            <div className="absolute top-2 right-2 flex gap-1">
              <button
                className="p-1.5 rounded-full bg-white text-neutral-700 shadow-md hover:bg-neutral-100 disabled:opacity-50"
                onClick={() => generateVariation(image.id)}
                disabled={isGeneratingVariation}
                title="Variation"
              >
                {isGeneratingVariation ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" />
                ) : (
                  <Layers className="h-4 w-4" />
                )}
              </button>
              <button
                className="p-1.5 rounded-full bg-white text-neutral-700 shadow-md hover:bg-neutral-100 disabled:opacity-50"
                onClick={() => downloadImage(image.id)}
                disabled={isDownloading}
                title={t('common.download')}
              >
                {isDownloading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Mitte: Lupe */}
            <button
              className="p-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg hover:scale-105 transition-all"
              onClick={onClick}
              title={t('gallery.enlarge')}
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Unten links: Veröffentlichen + Löschen */}
            <div className="absolute bottom-2 left-2 flex gap-1">
              {!image.is_public_image && (
                <button
                  className="p-1.5 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-md disabled:opacity-50"
                  onClick={() => setShowConfirmPublish(true)}
                  disabled={isPublishing}
                  title={t('common.publish')}
                >
                  <Globe className="h-4 w-4" />
                </button>
              )}
              <button
                className="p-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-md disabled:opacity-50"
                onClick={() => setShowConfirmDelete(true)}
                disabled={isDeleting}
                title={t('common.delete')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* Unten rechts: Info */}
            <div className="absolute bottom-2 right-2 flex items-center gap-2 text-xs text-white/80">
              {image.tag_count > 0 && (
                <span className="flex items-center gap-0.5">
                  <Tag className="h-3 w-3" />
                  {image.tag_count}
                </span>
              )}
              {image.folder_count > 0 && (
                <span className="flex items-center gap-0.5">
                  <Folder className="h-3 w-3" />
                  {image.folder_count}
                </span>
              )}
              <span>{image.ratio || '?'}</span>
            </div>
          </div>
        )}

        {/* Bestätigung Löschen */}
        {showConfirmDelete && (
          <div className="absolute inset-0 z-20 bg-black/70 flex flex-col items-center justify-center gap-2 p-4">
            <p className="text-white text-sm font-medium">{t('gallery.deleteQuestion')}</p>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 rounded text-sm font-medium bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {t('common.yes')}
              </button>
              <button
                className="px-3 py-1.5 rounded text-sm font-medium bg-neutral-600 hover:bg-neutral-700 text-white"
                onClick={() => setShowConfirmDelete(false)}
              >
                {t('common.no')}
              </button>
            </div>
          </div>
        )}

        {/* Bestätigung Veröffentlichen */}
        {showConfirmPublish && (
          <div className="absolute inset-0 z-20 bg-black/70 flex flex-col items-center justify-center gap-2 p-4">
            <p className="text-white text-sm font-medium">{t('gallery.publishQuestion')}</p>
            <p className="text-amber-400 text-xs flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {t('gallery.publishNotReversible')}
            </p>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 rounded text-sm font-medium bg-green-500 hover:bg-green-600 text-white disabled:opacity-50"
                onClick={handlePublish}
                disabled={isPublishing}
              >
                {t('common.yes')}
              </button>
              <button
                className="px-3 py-1.5 rounded text-sm font-medium bg-neutral-600 hover:bg-neutral-700 text-white"
                onClick={() => setShowConfirmPublish(false)}
              >
                {t('common.no')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Rating-Leiste – IMMER sichtbar */}
      <div
        className="flex items-center justify-center py-1.5 px-2 bg-white dark:bg-neutral-900 border-t border-neutral-100 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <ImageRatingInput
          value={image.user_rating ?? null}
          onChange={handleRatingChange}
          disabled={isRatingPending}
          size="sm"
        />
      </div>
    </div>
  );
}
